/**
 * Pick the first browser locale that matches an available translation code.
 * Matches full tag (case-insensitive), then base language (e.g. pl-PL -> pl).
 *
 * @param {{ code: string }[]} translations
 * @returns {string|null} The matched translation `code` as stored on the option, or null.
 */
export function matchBrowserLanguageToTranslationCodes(translations) {
  if (!translations || translations.length === 0 || typeof navigator === 'undefined') {
    return null
  }

  const normalizedCodes = translations.map((t) => ({
    original: String(t.code),
    lower: String(t.code).toLowerCase().replace(/_/g, '-'),
  }))

  const hasCode = (lower) => normalizedCodes.some((c) => c.lower === lower)

  const resolveOriginal = (lower) => {
    const found = normalizedCodes.find((c) => c.lower === lower)
    return found ? found.original : null
  }

  /** @type {string[]} */
  const candidates = []
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    candidates.push(...navigator.languages)
  } else if (navigator.language) {
    candidates.push(navigator.language)
  }

  for (const raw of candidates) {
    if (!raw || typeof raw !== 'string') {
      continue
    }

    const tag = raw.trim().replace(/_/g, '-')
    const lowerFull = tag.toLowerCase()

    if (hasCode(lowerFull)) {
      return resolveOriginal(lowerFull)
    }

    const base = lowerFull.split('-')[0]
    if (base && hasCode(base)) {
      return resolveOriginal(base)
    }
  }

  return null
}
